import { Plus, Trash2, BadgeCheck, Handshake, MonitorSmartphone, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BrandButton } from "@/components/PageHeader";
import { shortId } from "@/lib/api";
import { SortableList } from "./SortableList";

export const FixedCategoryBlock = ({ value, onChange, title, description, onLabel, testId, tone = "slate" }) => (
  <div className={`mt-3 rounded-md border p-4 flex flex-wrap items-center gap-3 ${tone === "emerald" ? "border-emerald-200 bg-emerald-50/40" : "border-[#E5E7EB] bg-white"}`} data-testid={`setup-${testId}-block`}>
    <ShieldCheck className={`w-5 h-5 ${tone === "emerald" ? "text-emerald-600" : "text-gray-500"}`} />
    <div className="flex-1 min-w-[180px]">
      <div className="font-semibold text-sm">{title}</div>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    {value.enabled && (
      <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">Fee (GH₵)
        <Input type="number" min="0" value={value.fee} onChange={(e) => onChange({ ...value, fee: Math.max(0, Number(e.target.value) || 0) })} className="w-28 h-9 font-mono bg-white" data-testid={`setup-${testId}-fee`} />
      </label>
    )}
    <label className="flex items-center gap-2 text-sm font-semibold">
      <Switch checked={value.enabled} onCheckedChange={(enabled) => onChange({ ...value, enabled })} data-testid={`setup-${testId}-toggle`} />
      {value.enabled ? onLabel : "Off"}
    </label>
  </div>
);

export const GoodStandingBlock = ({ value, onChange }) => (
  <FixedCategoryBlock value={value} onChange={onChange} testId="gs" tone="emerald" title="Member in Good Standing" onLabel="Shown to Good Standing"
    description="Shown only to members whose GAMLS ID or Staff ID is on the membership master list. It is their default category." />
);

export const OnlineToggle = ({ value, onChange }) => (
  <div className="mt-3 rounded-md border border-[#E5E7EB] p-4 flex flex-wrap items-center gap-3" data-testid="setup-online-block">
    <MonitorSmartphone className="w-5 h-5 text-[#E60000]" />
    <div className="flex-1 min-w-[180px]">
      <div className="font-semibold text-sm">Online attendance</div>
      <p className="text-xs text-gray-500">When on, registrants are asked "How do you want to attend this event?" (In-Person or Online). Online attendees pay the same fees and don't see accommodation.</p>
    </div>
    <label className="flex items-center gap-2 text-sm font-semibold">
      <Switch checked={value} onCheckedChange={onChange} data-testid="setup-online-toggle" />
      {value ? "Asked on form" : "In-person only"}
    </label>
  </div>
);

export const SponsorToggle = ({ value, onChange }) => (
  <div className="mt-3 rounded-md border border-[#E5E7EB] p-4 flex flex-wrap items-center gap-3" data-testid="setup-sponsor-block">
    <Handshake className="w-5 h-5 text-[#E60000]" />
    <div className="flex-1 min-w-[180px]">
      <div className="font-semibold text-sm">Sponsor</div>
      <p className="text-xs text-gray-500">A fixed, free category for sponsor representatives. Sponsors can collect souvenirs without payment.</p>
    </div>
    <label className="flex items-center gap-2 text-sm font-semibold">
      <Switch checked={value.enabled} onCheckedChange={(enabled) => onChange({ enabled })} data-testid="setup-sponsor-toggle" />
      {value.enabled ? "Shown on form" : "Hidden"}
    </label>
  </div>
);

export const ProtocolBlock = ({ value, onChange }) => {
  const subs = value.subcategories;
  const setSubs = (subcategories) => onChange({ ...value, subcategories });
  const update = (id, patch) => setSubs(subs.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const toggle = (enabled) => onChange({ enabled, subcategories: enabled && !subs.length ? [{ id: shortId(), name: "", fee: 0 }] : subs });
  return (
    <div className="mt-6 rounded-md border border-[#E5E7EB] p-4" data-testid="setup-protocol-block">
      <div className="flex flex-wrap items-center gap-3">
        <BadgeCheck className="w-5 h-5 text-[#E60000]" />
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold text-sm">Protocol</div>
          <p className="text-xs text-gray-500">A fixed category. When on, registrants who pick Protocol then choose their exact subcategory.</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <Switch checked={value.enabled} onCheckedChange={toggle} data-testid="setup-protocol-toggle" />
          {value.enabled ? "Shown on form" : "Hidden"}
        </label>
      </div>
      {value.enabled && (
        <div className="mt-4 space-y-3" data-testid="setup-protocol-list">
          <div className="hidden md:grid grid-cols-[84px_1fr_160px_40px] gap-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 px-1"><span>Order</span><span>Subcategory name</span><span>Fee (GH₵)</span><span /></div>
          <SortableList items={subs} onChange={setSubs} testId="setup-protocol-move" render={(s, i, controls) => (
            <div className="grid grid-cols-1 md:grid-cols-[84px_1fr_160px_40px] gap-3 items-center rounded-md bg-[#F8F9FA] md:bg-white p-3 md:p-0" data-testid={`setup-protocol-row-${i}`}>
              {controls}
              <Input value={s.name} onChange={(e) => update(s.id, { name: e.target.value })} placeholder="e.g. Invited Guest" data-testid={`setup-protocol-name-${i}`} />
              <Input type="number" min="0" value={s.fee} onChange={(e) => update(s.id, { fee: Number(e.target.value) || 0 })} className="font-mono" data-testid={`setup-protocol-fee-${i}`} />
              <button type="button" onClick={() => setSubs(subs.filter((x) => x.id !== s.id))} disabled={subs.length === 1} className="w-10 h-10 grid place-items-center rounded-md text-gray-500 hover:text-[#E60000] hover:bg-[#FFF0F0] disabled:opacity-30" data-testid={`setup-protocol-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          )} />
          <BrandButton type="button" variant="outline" onClick={() => setSubs([...subs, { id: shortId(), name: "", fee: 0 }])} data-testid="setup-protocol-add"><Plus className="w-4 h-4" />Add subcategory</BrandButton>
        </div>
      )}
    </div>
  );
};
